import { Component, Input, inject, computed, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { PhaseUiModel } from '../../models/LearningPlanInstanceDetailUIModel';

import { ArrowWrapperComponent } from '../../../../../shared/components/arrow-wrapper/arrow-wrapper.component';
import { LinkComponent } from '../../../../../shared/components/link/link.component';
import { MatDialog } from '@angular/material/dialog';
import { CoursePreviewService } from '../../../course/services/course-preview.service';
import { SlideToggleComponent } from '../../../../../shared/components/slide-toggle/slide-toggle.component';
import { LearningPlanDataService } from '../../services/learning-plan-data.service';
import { LearningPlanDialogService } from '../../services/learning-plan-dialog.service';
import { MatTooltip } from '@angular/material/tooltip';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';

const TRAINING_PHASE_TYPE_TO_FREQUENCY_KEY: Partial<Record<string, string>> = {
  ['Monthly']: 'month',
  ['Quarterly']: 'quarter',
  ['Yearly']: 'year',
};

@Component({
  selector: 'app-learning-plan-structure',
  imports: [
    MatIcon,
    TranslocoDirective,
    ArrowWrapperComponent,
    LinkComponent,
    SlideToggleComponent,
    MatTooltip,
  ],
  templateUrl: './learning-plan-structure.component.html',
})
export class LearningPlanStructureComponent {
  dialog = inject(MatDialog);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly coursePreviewService = inject(CoursePreviewService);
  private readonly learningPlanDataService = inject(LearningPlanDataService);
  private readonly learningPlanDialogService = inject(
    LearningPlanDialogService,
  );

  loopingEnabled = computed(() => {
    const details = this.learningPlanDataService.details();
    return details?.canToggleRepetition ?? true;
  });

  readonly phaseFrequencyKey = computed(() => {
    const trainingPhaseType =
      this.learningPlanDataService.details()?.trainingPhaseType;
    return trainingPhaseType
      ? TRAINING_PHASE_TYPE_TO_FREQUENCY_KEY[trainingPhaseType]
      : undefined;
  });

  @Input() phases: PhaseUiModel[] = [];
  @Input() set externalArrowTrigger(value: number | undefined) {
    if (value !== undefined) {
      this.externalArrowTriggerSignal.set(value);
    }
  }
  @Input() looping = false;
  @Input() hasDeadline = false;

  private readonly externalArrowTriggerSignal = signal(0);

  readonly arrowRedrawSignal = computed(
    () => {
      this.learningPlanDataService.arrowRedrawTrigger();
      this.externalArrowTriggerSignal();
      return 0;
    },
    { equal: () => false },
  );

  onCoursePreviewClick(courseId: string) {
    return () => void this.coursePreviewService.open(courseId);
  }

  onToggleClick(event: Event) {
    if (!this.loopingEnabled()) {
      return;
    }
    // Prevent the default toggle behavior
    event.preventDefault();
    event.stopPropagation();

    this.learningPlanDialogService.showLoopingConfirmationDialog(
      !this.looping,
      () => {
        this.learningPlanDataService.setLooping(!this.looping);
      },
    );
  }
}
